/**
 * IXD 위치 포커싱 재시도 루프 — 라이브 화면과 다운로드 뷰어가 공유한다.
 *
 * 대상 chunk 가 막 마운트된 직후에는 DOM 이 아직 없을 수 있어 한 번에 실패한다. 두 화면이
 * 각자 다른 재시도 정책을 쓰면(한쪽은 10회, 한쪽은 rAF 1회) 같은 조작이 한쪽에서만
 * "이동 못 했습니다" 경고를 띄운다 — 뷰어는 임베드 데이터라 대개 1회로 성공하지만 그
 * 전제는 lazy/virtualized 렌더가 들어오는 순간 깨진다.
 */

export const FOCUS_RETRY_INTERVAL_MS = 100;
export const FOCUS_RETRY_MAX_ATTEMPTS = 10;

/** 재시도해도 못 갔을 때 두 화면이 같이 쓰는 문구. */
export const FOCUS_FAILED_MESSAGE = "선택한 위치로 이동하지 못했습니다.";

/**
 * `attempt` 가 true 를 돌려줄 때까지 재시도한다. 반환값은 정리 함수(effect cleanup 용).
 *
 * **첫 시도는 즉시** 하고 실패했을 때만 인터벌을 태운다 — 대상 DOM 이 이미 있는 흔한
 * 경로(뷰어는 임베드 데이터라 대개 여기다)에서 100ms 를 그냥 기다리면 클릭 반응이
 * 그만큼 늦게 보인다. 재시도 정책을 공유하는 목적은 첫 시도를 늦추지 않고 달성된다.
 */
export const focusWithRetry = (
  attempt: () => boolean,
  handlers: { onSuccess?: () => void; onGiveUp?: () => void } = {},
): (() => void) => {
  let attempts = 0;
  let timerId: number | undefined;

  const run = () => {
    timerId = undefined;
    attempts += 1;
    if (attempt()) {
      handlers.onSuccess?.();
      return;
    }
    if (attempts < FOCUS_RETRY_MAX_ATTEMPTS) {
      schedule();
      return;
    }
    handlers.onGiveUp?.();
  };

  const schedule = () => {
    timerId = window.setTimeout(run, FOCUS_RETRY_INTERVAL_MS);
  };

  run();
  return () => {
    if (timerId !== undefined) window.clearTimeout(timerId);
  };
};
